import {Component, DestroyRef, inject, Input, OnChanges, Output, EventEmitter, SimpleChanges} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatTooltip} from '@angular/material/tooltip';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {TitleCasePipe} from '@angular/common';
import {SelectOneComponent} from '../../../../shared/components/select-one/select-one.component';
import {BalaoInformativoComponent} from '../../../../shared/components/balao-informativo/balao-informativo.component';
import {CelularMaskPipe} from '../../../../shared/pipe/celular-mask.pipe';
import {CpfCnpjMaskPipe} from '../../../../shared/pipe/cpf-cnpj-mask.pipe';
import {SetorService} from '../../../../shared/service/setor.service';
import {PlataformaService} from '../../../../shared/service/plataforma.service';
import {MsgService} from '../../../../shared/service/msg.service';
import {SetorVinculoBase} from '../../../../shared/model/setor/setor-vinculo-base.model';
import {ContatoBaseDinamica} from '../../../../shared/model/plataforma/dados-base-dinamica.model';
import {DisparoAutomaticoModel} from '../../../../shared/model/disparo-automatico/disparo-automatico.model';
import {TabelaSkeletonComponent} from '../../../../shared/components/tabela-skeleton/tabela-skeleton.component';
import {MatDialog} from '@angular/material/dialog';
import {ModalBaseCompletaComponent} from '../modal-base-completa/modal-base-completa.component';
import {PlataformaModel} from '../../../../shared/model/plataforma-model';
import {DisparoAutomaticoVariavelModel} from '../../../../shared/model/disparo-automatico/disparo-automatico-variavel.model';

@Component({
  selector: 'app-contatos-base-dados',
  standalone: true,
  imports: [
    MatTableModule,
    MatTooltip,
    MatProgressSpinner,
    TitleCasePipe,
    SelectOneComponent,
    BalaoInformativoComponent,
    CelularMaskPipe,
    CpfCnpjMaskPipe,
    TabelaSkeletonComponent
  ],
  templateUrl: './contatos-base-dados.component.html',
  styleUrl: './contatos-base-dados.component.css'
})
export class ContatosBaseDadosComponent implements OnChanges {
  @Input() setorId?: number;
  @Input() disparoAutomatico: DisparoAutomaticoModel = new DisparoAutomaticoModel();
  @Input() podeEditar = true;
  @Input() variaveis: DisparoAutomaticoVariavelModel[] = [];
  @Input() exibirReenvioIntegracao = false;
  @Input() reenviandoIntegracao = false;

  @Output() colunasComplementaresAtualizadas = new EventEmitter<string[]>();
  @Output() contatosPreviewAtualizados = new EventEmitter<ContatoBaseDinamica[]>();
  @Output() reenviarIntegracao = new EventEmitter<void>();

  private readonly setorService = inject(SetorService);
  private readonly plataformaService = inject(PlataformaService);
  private readonly msgService = inject(MsgService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  plataformas: PlataformaModel[] = [];
  plataformaSelecionada?: PlataformaModel;
  bases: SetorVinculoBase[] = [];
  baseSelecionada?: SetorVinculoBase;

  contatos: ContatoBaseDinamica[] = [];
  contatosTable = new MatTableDataSource<ContatoBaseDinamica>();
  colunasComplementares: string[] = [];
  displayedColumns: string[] = ['nome', 'cpf', 'telefone', 'email'];
  totalContatos?: number;

  fimDaLista = false;
  isCarregando = false;
  carregandoBases = false;
  carregandoPlataformas = false;
  private paginaAtual = 0;
  private carregamentoId = 0;
  private readonly PAGE_SIZE = 5;

  ngOnChanges(changes: SimpleChanges): void {
    const mudouPlataforma = changes['disparoAutomatico']
      && this.disparoAutomatico.plataformaBaseDinamicaId !== this.plataformaSelecionada?.id;
    if ((changes['setorId'] || mudouPlataforma) && this.setorId) {
      this.carregarPlataformas();
    }
  }

  carregarPlataformas(): void {
    this.carregandoPlataformas = true;
    this.plataformaService.buscarPlataformasDoGrupo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.plataformas = (res ?? []).filter(plataforma => plataforma.ativo !== false);
          const plataformaId = this.disparoAutomatico.plataformaBaseDinamicaId;
          if (plataformaId) {
            const plataforma = this.plataformas.find(item => item.id === plataformaId);
            if (plataforma) this.selecionarPlataforma(plataforma);
          }
        },
        error: () => {
          this.carregandoPlataformas = false;
          this.msgService.msgErro('Não foi possível carregar as plataformas.');
        },
        complete: () => this.carregandoPlataformas = false
      });
  }

  selecionarPlataforma(plataforma: PlataformaModel | undefined): void {
    const baseIdRestaurar = plataforma?.id === this.disparoAutomatico.plataformaBaseDinamicaId
      ? this.disparoAutomatico.setorVinculoBaseId
      : undefined;
    this.plataformaSelecionada = plataforma;
    this.disparoAutomatico.plataformaBaseDinamicaId = plataforma?.id;
    this.baseSelecionada = undefined;
    this.disparoAutomatico.setorVinculoBaseId = baseIdRestaurar;
    this.bases = [];
    this.limparDados();
    if (plataforma?.id) this.carregarBases(plataforma.id);
  }

  carregarBases(plataformaId: number): void {
    this.carregandoBases = true;
    this.setorService.buscarBasesDoSetor(this.setorId!, plataformaId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.bases = res ?? [];
          this.restaurarBaseSelecionada();
        },
        error: () => {
          this.carregandoBases = false;
          this.msgService.msgErro('Não foi possível carregar as bases do setor.');
        },
        complete: () => this.carregandoBases = false
      });
  }

  private restaurarBaseSelecionada(): void {
    const id = this.disparoAutomatico.setorVinculoBaseId;
    if (id) {
      const base = this.bases.find(b => b.id === id);
      if (base) {
        this.selecionarBase(base);
      } else {
        this.msgService.msgAdvertencia('A base dinâmica vinculada a este disparo não está mais ativa no setor. Selecione outra base.');
      }
    }
  }

  selecionarBase(base: SetorVinculoBase | undefined): void {
    this.baseSelecionada = base;
    this.disparoAutomatico.setorVinculoBaseId = base?.id;
    this.disparoAutomatico.origemContatos = 'BASE_DINAMICA';
    if (!base?.id) {
      this.limparDados();
      return;
    }
    this.carregarDados(true);
  }

  private carregarDados(reset: boolean): void {
    if (!this.baseSelecionada?.id) {
      return;
    }
    if (reset) {
      this.limparDados();
    }
    if (this.isCarregando || this.fimDaLista) {
      return;
    }

    const geracao = ++this.carregamentoId;
    this.isCarregando = true;
    this.plataformaService.buscarDadosBase(this.disparoAutomatico.setorVinculoBaseId!, this.paginaAtual, this.PAGE_SIZE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          if (geracao !== this.carregamentoId) {
            return;
          }
          const novos = res.contatos ?? [];
          if (reset) {
            this.montarColunas(novos);
            this.totalContatos = res.totalElements;
            this.contatosPreviewAtualizados.emit(novos.length ? [novos[0]] : []);
            if (novos.length === 1 && novos[0].teste) {
              this.msgService.msgAdvertencia(
                'Esta base está sem contatos no momento. Exibindo um contato de teste com todas as colunas para você configurar as variáveis.');
            }
          }
          this.contatos = [...this.contatos, ...novos];
          this.contatosTable.data = this.contatos;
          this.fimDaLista = !!res.last;
          this.paginaAtual++;
          this.isCarregando = false;
        },
        error: () => {
          if (geracao === this.carregamentoId) {
            this.isCarregando = false;
          }
          this.msgService.msgErro('Não foi possível carregar os contatos da base.');
        }
      });
  }

  private montarColunas(contatos: ContatoBaseDinamica[]): void {
    const complementares = contatos.length > 0 && contatos[0].camposComplementares
      ? Object.keys(contatos[0].camposComplementares)
      : [];
    this.colunasComplementares = complementares;
    this.displayedColumns = ['nome', 'cpf', 'telefone', 'email'];
    this.colunasComplementaresAtualizadas.emit(complementares);
  }

  private limparDados(): void {
    this.carregamentoId++;
    this.paginaAtual = 0;
    this.fimDaLista = false;
    this.contatos = [];
    this.contatosTable.data = [];
    this.colunasComplementares = [];
    this.displayedColumns = ['nome', 'cpf', 'telefone', 'email'];
    this.totalContatos = undefined;
    this.isCarregando = false;
    this.colunasComplementaresAtualizadas.emit([]);
    this.contatosPreviewAtualizados.emit([]);
  }

  buscarCampoComplementar(elemento: ContatoBaseDinamica, coluna: string): string {
    return elemento.camposComplementares?.[coluna] ?? '--';
  }

  abrirBaseCompleta(): void {
    if (!this.baseSelecionada?.id) return;

    this.dialog.open(ModalBaseCompletaComponent, {
      width: '90vw',
      maxWidth: '90vw',
      height: '100vh',
      position: {top: '0', right: '0'},
      panelClass: 'slide-modal',
      data: {
        baseId: this.baseSelecionada.id,
        colunasComplementares: this.colunasComplementares,
        totalContatos: this.totalContatos,
        tipoDisparo: this.disparoAutomatico.tipoDisparo,
        templateSms: this.disparoAutomatico.template,
        variaveis: this.variaveis
      }
    });
  }
}
